import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtNL } from "@/lib/dates";
import { Plus, Truck, AlertTriangle, X, Edit2, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const VEHICLE_TYPES = ["bus", "bakwagen", "pickup", "aanhanger", "kraan"];

export default function Voertuigen() {
  const { user } = useAuth();
  const office = ["OWNER", "PLANNER"].includes(user?.role);
  const [vehicles, setVehicles] = useState([]);
  const [showModal, setShowModal] = useState(null); // null | "new" | vehicle object
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => setVehicles((await api.get("/vehicles")).data);
  useEffect(() => { load(); }, []);

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/vehicles/${confirmDelete.id}`);
      toast.success("Voertuig verwijderd");
      setConfirmDelete(null);
      await load();
    } catch { toast.error("Kon niet verwijderen"); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="pb-heading text-3xl font-black">VOERTUIGEN</h1>
        {office && (
          <button className="pb-btn-primary flex items-center gap-2" onClick={() => setShowModal("new")} data-testid="btn-new-vehicle">
            <Plus size={18}/> Nieuw voertuig
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {vehicles.map((v) => {
          const apkDays = v.apk_date ? Math.round((new Date(v.apk_date) - new Date()) / (1000*60*60*24)) : null;
          const apkWarn = apkDays !== null && apkDays <= 60;
          return (
            <div key={v.id} className="pb-card p-4" data-testid={`vehicle-card-${v.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white rounded"><Truck size={26}/></div>
                <div className="flex-1">
                  <div className="pb-heading text-lg font-black">{v.name}</div>
                  <div className="text-sm font-semibold text-slate-700">{v.license_plate} · {v.type}</div>
                  {v.contents && <div className="text-xs text-slate-600 mt-1 font-medium">{v.contents}</div>}
                </div>
                {office && (
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setShowModal(v)} className="p-2 border-2 border-slate-900 rounded hover:bg-orange-50" data-testid={`btn-edit-vehicle-${v.id}`} aria-label="Bewerken"><Edit2 size={14}/></button>
                    <button onClick={() => setConfirmDelete(v)} className="p-2 border-2 border-red-600 text-red-600 rounded hover:bg-red-50" data-testid={`btn-delete-vehicle-${v.id}`} aria-label="Verwijderen"><Trash2 size={14}/></button>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center justify-between border-t-2 border-slate-200 pt-2">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">APK verloopt</div>
                  <div className="font-black">{v.apk_date ? fmtNL(v.apk_date) : "—"}</div>
                </div>
                {apkWarn && (
                  <div className="flex items-center gap-1 text-orange-700 font-bold text-sm"><AlertTriangle size={16}/> {apkDays}d</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <VehicleModal
          vehicle={showModal === "new" ? null : showModal}
          onClose={() => setShowModal(null)}
          onSaved={() => { setShowModal(null); load(); }}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60">
          <div className="pb-card w-full max-w-sm p-5" data-testid="confirm-delete-vehicle">
            <div className="pb-heading text-lg font-black mb-2">Voertuig verwijderen?</div>
            <div className="text-sm text-slate-700 font-medium mb-4">{confirmDelete.name} ({confirmDelete.license_plate}) wordt definitief verwijderd.</div>
            <div className="flex gap-2">
              <button className="pb-btn-outline flex-1" onClick={() => setConfirmDelete(null)}>Annuleren</button>
              <button className="pb-btn-primary !bg-red-600 flex-1" onClick={doDelete} data-testid="confirm-delete-vehicle-ok">Verwijderen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VehicleModal({ vehicle, onClose, onSaved }) {
  const editing = !!vehicle;
  const [form, setForm] = useState({
    name: vehicle?.name || "",
    license_plate: vehicle?.license_plate || "",
    type: vehicle?.type || "bus",
    contents: vehicle?.contents || "",
    apk_date: vehicle?.apk_date || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/vehicles/${vehicle.id}`, form);
        toast.success("Voertuig bijgewerkt");
      } else {
        await api.post("/vehicles", form);
        toast.success("Voertuig aangemaakt");
      }
      onSaved();
    } catch { toast.error("Fout bij opslaan"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50">
      <form onSubmit={submit} className="pb-card w-full max-w-md" data-testid="vehicle-modal">
        <div className="bg-slate-900 text-white p-4 flex items-center justify-between">
          <div className="pb-heading font-black text-lg">{editing ? "Voertuig bewerken" : "Nieuw voertuig"}</div>
          <button type="button" onClick={onClose} className="text-white"><X size={20}/></button>
        </div>
        <div className="p-4 space-y-3">
          <input className="pb-input" placeholder="Roepnaam (bijv. Bus 3)" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} required data-testid="nv-name"/>
          <input className="pb-input" placeholder="Kenteken" value={form.license_plate} onChange={(e)=>setForm({...form,license_plate:e.target.value})} data-testid="nv-plate"/>
          <select className="pb-input" value={form.type} onChange={(e)=>setForm({...form,type:e.target.value})} data-testid="nv-type">
            {VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
          </select>
          <textarea className="pb-input min-h-[80px] py-2" placeholder="Vaste inhoud (gereedschap etc.)" value={form.contents} onChange={(e)=>setForm({...form,contents:e.target.value})}/>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-700">APK verloopdatum</label>
          <input className="pb-input" type="date" value={form.apk_date || ""} onChange={(e)=>setForm({...form,apk_date:e.target.value})} data-testid="nv-apk"/>
        </div>
        <div className="p-4 border-t-2 border-slate-900 flex gap-2">
          <button type="button" onClick={onClose} className="pb-btn-outline flex-1">Annuleren</button>
          <button type="submit" disabled={saving} className="pb-btn-primary flex-1" data-testid="nv-save">{saving ? "Opslaan…" : (editing ? "Opslaan" : "Aanmaken")}</button>
        </div>
      </form>
    </div>
  );
}
